import React from "react";
import { Link } from "react-router-dom";
import { BadgeDollarSign, Sparkles, ShieldCheck, Check, ArrowRight } from "lucide-react";
import HostApplicationForm from "../components/HostApplicationForm";

const PILLARS = [
  { title: "We handle everything", desc: "Listing, pricing, guest communications, cleaning coordination, turnover, concierge add-ons." },
  { title: "Transparent 10%", desc: "A flat 10% of rental revenue — no hidden setup fees, no lock-ins, no surprises." },
  { title: "Janté-quality standards", desc: "Guests arrive pre-vetted via Sasha; add-ons (chef, grocery, housekeeping) ready on demand." },
  { title: "Monthly statements", desc: "Clear reporting of bookings, revenue, concierge upsells, guest feedback." },
];

export default function PropertyAssistancePage() {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16" data-testid="pa-page">
      <Link to="/hosts" className="btn-ghost inline-flex mb-6">← All hosts</Link>

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-end">
        <div>
          <div className="overline"><span className="text-[var(--brand-red)]">JTL Property Assistance</span> · Full-service hosting</div>
          <h1 className="font-display text-5xl sm:text-6xl text-[var(--emerald)] mt-5 leading-[1.05]">
            Let Janté run your property.<br />
            <em className="not-italic text-[var(--gold)]">You earn. We sweat.</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-5 max-w-xl">
            Full-service hosting or co-hosting by Janté's operations team. We list, market, book, communicate,
            turn over, and upsell concierge add-ons. You get monthly statements and hassle-free income.
          </p>
          <div className="mt-5 inline-flex items-center gap-3 px-4 py-2 border border-[var(--gold)] text-[var(--emerald)] text-sm">
            <BadgeDollarSign size={15} strokeWidth={1.5} className="text-[var(--gold)]" />
            <span>10% of rental revenue — that's it</span>
          </div>
        </div>
        <div className="lux-card-dark">
          <Sparkles size={20} strokeWidth={1.25} className="text-[var(--gold)]" />
          <div className="overline text-[var(--gold)] mt-3">Ideal for</div>
          <ul className="mt-3 space-y-1.5 text-sm opacity-95">
            <li>· Second-home owners</li>
            <li>· Investors who don't want the headache</li>
            <li>· Hosts scaling past 2–3 properties</li>
            <li>· Anyone tired of Airbnb-style noise</li>
          </ul>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-14">
        {PILLARS.map((p) => (
          <div key={p.title} className="lux-card">
            <Check size={16} strokeWidth={1.5} className="text-[var(--gold)]" />
            <h3 className="font-display text-2xl text-[var(--emerald)] mt-3">{p.title}</h3>
            <p className="text-sm text-[var(--ink-2)] mt-2">{p.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-16">
        <div className="overline">Apply</div>
        <h2 className="font-display text-4xl text-[var(--emerald)] mt-2">
          Tell us about the property we'll run.
        </h2>
        <HostApplicationForm
          program="property_assistance"
          title="Property Assistance application"
          cta="Apply — Property Assistance"
        />
      </div>

      <div className="mt-10 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
        <ShieldCheck size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
        No fees until your property is live. Review ratings, rates, and occupancy monthly.
      </div>
    </div>
  );
}
